import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Input, Select, Skeleton, Tooltip } from "antd";
import { ArrowLeftOutlined, SaveOutlined, GlobalOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import {
  fetchTemplateDetails,
  updateGlobalTemplate,
} from "../cms/services/globalTemplateService";
import { isSuperAdmin } from "../cms/utils/role";
import AccessDenied from "../AccessDenied";

/**
 * Edit a GLOBAL email template.
 *
 * Its own screen and its own save endpoint on purpose: the lead/customer editor
 * posts to update-email-template-details, which answers 422 for any other
 * context (subscription, auth, wallet …). Only a superadmin may open this — the
 * API enforces it too and replies 403.
 */

/** `placeholders` arrives as a JSON array string, but older rows may hold a
 *  comma-separated list — both are accepted, and the keys come back bare. */
const parsePlaceholders = (raw) => {
  if (!raw) return [];
  let list = raw;
  if (typeof raw === "string") {
    const text = raw.trim();
    try {
      list = JSON.parse(text);
    } catch {
      list = text.split(",");
    }
  }
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list
    .map((v) => String(v ?? "").trim().replace(/^\{\{|\}\}$/g, "").trim())
    .filter((v) => {
      if (!v || seen.has(v)) return false;
      seen.add(v);
      return true;
    });
};

/** Only what a template engine can actually resolve: letters, digits, underscore. */
const cleanKey = (v) =>
  String(v ?? "").trim().replace(/^\{\{|\}\}$/g, "").trim().replace(/[^A-Za-z0-9_]/g, "_");

const label = { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" };
const box = { background: "#fff", borderRadius: 14, border: "1px solid #eef2ff", boxShadow: "0 2px 14px rgba(0,0,0,0.05)", padding: 22, marginBottom: 18 };

export default function EditGlobalTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const allowed = isSuperAdmin();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ template_name: "", subject: "", body: "", status: "active" });
  const [meta, setMeta] = useState({ context_type: "", template_type: "", service_name: "" });
  /* The template's own placeholders, straight from the detail response
     (`placeholders` is a JSON array string there). They can be added, renamed and
     removed here, and go back with the save. */
  const [placeholders, setPlaceholders] = useState([]);
  const [newPlaceholder, setNewPlaceholder] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  /* Global templates are complete HTML documents (doctype, <head>, <style>,
     <body>). A contentEditable keeps only what lives inside <body> — the wrapper
     is dropped the moment the HTML is assigned to it, and saving would then
     destroy the template. So the source IS the editor here, with a preview
     beside it; the WYSIWYG editor stays on the lead/customer screens, whose
     bodies are fragments. */
  const [bodyMode, setBodyMode] = useState("html");   // "html" | "preview"
  const bodyRef = useRef(null);          // <textarea> holding the raw HTML
  const lastFocused = useRef("body");    // "body" | "subject" | "template_name"
  const subjectRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    if (!allowed || !id) return;
    let alive = true;
    setLoading(true);
    fetchTemplateDetails(id)
      .then((t) => {
        if (!alive) return;
        setForm({
          template_name: t.template_name || "",
          subject: t.subject || "",
          body: t.body || "",
          status: t.status || "active",
        });
        setMeta({
          context_type: t.context_type || "",
          template_type: String(t.template_type ?? ""),
          service_name: t.service_name || "",
        });
        setPlaceholders(parsePlaceholders(t.placeholders));
      })
      .catch((err) => {
        toast.error(err.message);
        navigate("/cms/templates/global");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return <AccessDenied />;

  /** Drop a placeholder wherever the user last had the cursor. */
  const insertPlaceholder = (token) => {
    const target = lastFocused.current;
    const el =
      target === "body"
        ? bodyRef.current
        : (target === "subject" ? subjectRef : nameRef).current?.input ||
          (target === "subject" ? subjectRef : nameRef).current;
    const field = target === "body" ? "body" : target === "subject" ? "subject" : "template_name";
    const text = form[field] || "";
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    const next = text.slice(0, start) + token + text.slice(end);
    setForm((p) => ({ ...p, [field]: next }));
    // Put the caret back after what was just inserted.
    requestAnimationFrame(() => {
      if (!el?.setSelectionRange) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const addPlaceholder = () => {
    const key = cleanKey(newPlaceholder);
    if (!key) return;
    if (placeholders.includes(key)) {
      toast.info(`${key} is already in the list`);
      setNewPlaceholder("");
      return;
    }
    setPlaceholders((prev) => [...prev, key]);
    setNewPlaceholder("");
  };

  const removePlaceholder = (key) => {
    /* Only the list entry goes — the token is left alone wherever it is already
       written, so nothing silently disappears from the mail. */
    setPlaceholders((prev) => prev.filter((k) => k !== key));
  };

  const commitRename = () => {
    const next = cleanKey(editingValue);
    const prevKey = placeholders[editingIndex];
    if (editingIndex === null || !next || next === prevKey) {
      setEditingIndex(null);
      return;
    }
    if (placeholders.includes(next)) {
      toast.error(`${next} already exists`);
      return;
    }
    /* Rename it everywhere it is used too — a renamed key left behind in the
       body would render as literal text in the sent mail. */
    const swap = (text) => String(text || "").split(`{{${prevKey}}}`).join(`{{${next}}}`);
    setForm((p) => ({ ...p, subject: swap(p.subject), body: swap(p.body) }));
    setPlaceholders((prev) => prev.map((k, i) => (i === editingIndex ? next : k)));
    setEditingIndex(null);
  };

  const handleSave = async () => {
    if (!form.template_name.trim()) return toast.error("Template name is required");
    if (!form.subject.trim()) return toast.error("Subject is required");
    if (!form.body.trim()) return toast.error("Email body cannot be empty");

    const confirm = await Swal.fire({
      title: "Update global template?",
      html: `<b>${form.template_name}</b> is a system template — the change applies everywhere it is used.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      confirmButtonColor: "#4338ca",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      await updateGlobalTemplate(id, {
        template_name: form.template_name.trim(),
        subject: form.subject.trim(),
        body: form.body,
        status: form.status,
        // Same shape the API returns it in.
        placeholders: JSON.stringify(placeholders),
      });
      toast.success("Global template updated");
      navigate("/cms/templates/global");
    } catch (err) {
      // 403 (not a superadmin) and 422 land here with the API's own wording.
      Swal.fire("Could not update", err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg,#4338ca 0%,#312e81 100%)", borderRadius: 16, padding: "24px 28px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, boxShadow: "0 8px 30px rgba(67,56,202,0.25)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button
                onClick={() => navigate("/cms/templates/global")}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.32)", color: "#fff", borderRadius: 10, padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                <ArrowLeftOutlined /> Back
              </button>
              <div style={{ width: 48, height: 48, background: "rgba(255,255,255,0.18)", borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20 }}>
                <GlobalOutlined />
              </div>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontSize: 21, fontWeight: 700 }}>Edit Global Template</h4>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.16)", padding: "9px 16px", borderRadius: 10, fontSize: 13, color: "#fff", display: "flex", gap: 7 }}>
              <Link to="/cms/templates/global" style={{ color: "#fff", textDecoration: "none" }}>Global Templates</Link>
              <span style={{ opacity: 0.55 }}>›</span>
              <span style={{ fontWeight: 600 }}>Edit</span>
            </div>
          </div>

          {loading ? (
            <div style={box}><Skeleton active paragraph={{ rows: 8 }} /></div>
          ) : (
            <div className="row">
              {/* ── Form ── */}
              <div className="col-lg-8">
                <div style={box}>
                  <div style={{ marginBottom: 18 }}>
                    <label style={label}>Template Name <span style={{ color: "#ef4444" }}>*</span></label>
                    <Input
                      ref={nameRef}
                      value={form.template_name}
                      onChange={(e) => setForm((p) => ({ ...p, template_name: e.target.value }))}
                      onFocus={() => (lastFocused.current = "template_name")}
                      size="large" style={{ borderRadius: 9 }}
                    />
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <label style={label}>Subject <span style={{ color: "#ef4444" }}>*</span></label>
                    <Input
                      ref={subjectRef}
                      value={form.subject}
                      onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                      onFocus={() => (lastFocused.current = "subject")}
                      size="large" style={{ borderRadius: 9 }}
                    />
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <label style={label}>Email Body <span style={{ color: "#ef4444" }}>*</span></label>
                    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
                        {[
                          { key: "html", label: "HTML" },
                          { key: "preview", label: "Preview" },
                        ].map((m) => (
                          <button
                            key={m.key}
                            type="button"
                            onClick={() => setBodyMode(m.key)}
                            style={{
                              border: "1px solid " + (bodyMode === m.key ? "#4338ca" : "#e5e7eb"),
                              background: bodyMode === m.key ? "#4338ca" : "#fff",
                              color: bodyMode === m.key ? "#fff" : "#6b7280",
                              borderRadius: 7, padding: "4px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                            }}
                          >
                            {m.label}
                          </button>
                        ))}
                        <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af" }}>
                          Full HTML document — edited as source so the head and styles survive
                        </span>
                      </div>

                      {bodyMode === "html" ? (
                        <textarea
                          ref={bodyRef}
                          value={form.body}
                          onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                          onFocus={() => (lastFocused.current = "body")}
                          spellCheck={false}
                          style={{ width: "100%", minHeight: 460, border: "none", outline: "none", padding: 14, fontFamily: "monospace", fontSize: 12.5, lineHeight: 1.6, resize: "vertical", background: "#fbfcff", color: "#1a1a2e" }}
                        />
                      ) : (
                        /* sandboxed: a template is untrusted markup, and nothing in
                           it should be able to run against the CMS session. */
                        <iframe
                          title="Template preview"
                          sandbox=""
                          srcDoc={form.body}
                          style={{ width: "100%", minHeight: 460, border: "none", background: "#fff" }}
                        />
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 180 }}>
                      <label style={label}>Status</label>
                      <Select
                        value={form.status}
                        onChange={(v) => setForm((p) => ({ ...p, status: v }))}
                        style={{ width: "100%" }} size="large"
                        options={[
                          { value: "active", label: "Active" },
                          { value: "inactive", label: "Inactive" },
                        ]}
                      />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <label style={label}>Context</label>
                      {/* Read-only: a global template's context is what decides which
                          system flow sends it — changing it here would misroute mail. */}
                      <Input value={meta.context_type || "—"} disabled size="large" style={{ borderRadius: 9 }} />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <label style={label}>Template Type</label>
                      <Input
                        value={meta.template_type === "3" ? "Marketing (Promotional)" : meta.template_type === "4" ? "Service (Transactional)" : "—"}
                        disabled size="large" style={{ borderRadius: 9 }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginBottom: 24 }}>
                  <button
                    onClick={() => navigate("/cms/templates/global")}
                    style={{ padding: "10px 22px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 26px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#4338ca,#312e81)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, boxShadow: "0 4px 14px rgba(67,56,202,0.35)" }}
                  >
                    <SaveOutlined /> {saving ? "Saving…" : "Update Template"}
                  </button>
                </div>
              </div>

              {/* ── Placeholders ── */}
              <div className="col-lg-4">
                <div style={{ ...box, position: "sticky", top: 90, maxHeight: "calc(100vh - 130px)", overflowY: "auto" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Placeholders</div>
                    <span style={{ fontSize: 11, color: "#6b7280", background: "#f1f5f9", borderRadius: 20, padding: "2px 9px", fontWeight: 600 }}>
                      {placeholders.length}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
                    Click one to insert it at the cursor — it goes into the content as {"{{key}}"}
                    {meta.context_type ? ` · context: ${meta.context_type}` : ""}
                  </div>

                  {/* Add */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <Input
                      value={newPlaceholder}
                      onChange={(e) => setNewPlaceholder(e.target.value)}
                      onPressEnter={addPlaceholder}
                      placeholder="e.g. customer_name"
                      style={{ borderRadius: 8 }}
                    />
                    <button
                      onClick={addPlaceholder}
                      style={{ background: "#4338ca", color: "#fff", border: "none", borderRadius: 8, padding: "0 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Add
                    </button>
                  </div>

                  {placeholders.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: "#9ca3af" }}>
                      This template has no placeholders yet.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      {placeholders.map((key, i) => (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {editingIndex === i ? (
                            <>
                              <Input
                                autoFocus
                                size="small"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onPressEnter={commitRename}
                                onBlur={commitRename}
                                style={{ borderRadius: 7, fontFamily: "monospace", fontSize: 12 }}
                              />
                              <button
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={commitRename}
                                style={{ background: "#4338ca", color: "#fff", border: "none", borderRadius: 7, padding: "4px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}
                              >
                                Save
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => insertPlaceholder(`{{${key}}}`)}
                                title="Insert at cursor"
                                style={{ flex: 1, textAlign: "left", background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: 7, padding: "5px 9px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                {key}
                              </button>
                              <Tooltip title="Rename everywhere it is used">
                                <button
                                  onClick={() => { setEditingIndex(i); setEditingValue(key); }}
                                  style={{ background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 7, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}
                                >
                                  <EditOutlined />
                                </button>
                              </Tooltip>
                              <Tooltip title="Remove from the list">
                                <button
                                  onClick={() => removePlaceholder(key)}
                                  style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 7, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}
                                >
                                  <DeleteOutlined />
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
